import React, {useEffect, useState} from "react";
import {ClientStatusPill} from "./ConsoleParts";

const BTN = "h-9 px-3 text-sm font-semibold rounded-md border transition-colors";

function FeatureSwitch({label, on, onToggle}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!!on}
      onClick={onToggle}
      className="inline-flex items-center gap-2 px-3 h-9 rounded-md bg-ink-50 border border-ink-200 text-sm text-ink-700 hover:border-brand-400"
    >
      {label}
      <span className={`relative inline-block h-4 w-7 rounded-full ${on ? "bg-success-600" : "bg-ink-300"}`} aria-hidden="true">
        <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all ${on ? "left-3.5" : "left-0.5"}`} />
      </span>
      <b className="text-ink-900">{on ? "On" : "Off"}</b>
    </button>
  );
}

/**
 * One client's details and the actions the classic console already had: edit, activate or
 * deactivate, email alerts and passcode unlock, and the overspeed limit. Every change goes through
 * the console's confirm step (the handlers here only ask for it).
 */
export default function ClientHeader({row, onEdit, onToggleStatus, onToggleFeature, onSaveOverspeed}) {
  const c = row.client;
  const [speed, setSpeed] = useState(c.overspeedLimitKmh ?? "");
  useEffect(() => setSpeed(c.overspeedLimitKmh ?? ""), [c.clientId, c.overspeedLimitKmh]);
  const speedDirty = String(speed) !== String(c.overspeedLimitKmh ?? "");
  const active = c.status === "ACTIVE";

  return (
    <section className="bg-white border border-ink-200 rounded-xl p-4 flex flex-col gap-4" aria-label="Client details">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <span className="h-12 w-12 shrink-0 rounded-xl bg-brand-50 text-brand-700 grid place-items-center text-xl font-bold" aria-hidden="true">
            {(c.name || "?").charAt(0).toUpperCase()}
          </span>
          <div className="min-w-0">
            <h2 className="flex flex-wrap items-center gap-2 text-lg font-semibold text-ink-900">
              {c.name} <ClientStatusPill status={c.status} />
            </h2>
            <p className="text-sm text-ink-500">
              {[c.city, c.state].filter(Boolean).join(", ")} · Client ID {c.clientId}
            </p>
            <p className="text-sm text-ink-600 break-words">
              {c.contactName}
              {c.emailId && <> · <span className="select-all">{c.emailId}</span></>}
              {c.phoneNumber && <> · <span className="select-all">{c.phoneNumber}</span></>}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={onEdit} className={`${BTN} bg-white border-ink-300 text-ink-800 hover:bg-ink-50`}>
            Edit client
          </button>
          <button
            type="button"
            onClick={onToggleStatus}
            className={`${BTN} bg-white ${active ? "border-danger-100 text-danger-700 hover:bg-danger-50" : "border-success-100 text-success-700 hover:bg-success-50"}`}
          >
            {active ? "Deactivate" : "Activate"}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <FeatureSwitch label="Email alerts" on={c.emailNotificationEnabled} onToggle={() => onToggleFeature("emailNotificationEnabled")} />
        <FeatureSwitch label="Passcode unlock" on={c.passcodeFeature} onToggle={() => onToggleFeature("passcodeFeature")} />
        <form
          className="inline-flex items-center gap-2 px-3 h-9 rounded-md bg-ink-50 border border-ink-200 text-sm text-ink-700"
          onSubmit={(e) => {
            e.preventDefault();
            onSaveOverspeed(speed);
          }}
        >
          <label htmlFor="sac-overspeed">Overspeed limit</label>
          <input
            id="sac-overspeed"
            type="number"
            min="1"
            max="300"
            value={speed}
            placeholder="70"
            onChange={(e) => setSpeed(e.target.value)}
            className="w-16 h-7 px-1.5 rounded border border-ink-300 bg-white text-ink-900 tabular-nums"
          />
          <span>km/h</span>
          <button type="submit" disabled={!speedDirty} className="font-semibold text-brand-700 disabled:opacity-40">
            Save
          </button>
        </form>
        {c.overspeedLimitKmh == null && <span className="text-xs text-ink-500">Using the system default (70 km/h)</span>}
      </div>
    </section>
  );
}
